import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaWhatsapp, FaViber } from 'react-icons/fa';
import ContactForm from '@/components/sections/ContactForm';
import Seo from '@/components/seo/Seo';
import { content } from '@/content/sr';
import { externalUrls, safeExternalAnchor } from '@/config/envLinks';
import { BUSINESS_REF, breadcrumbJsonLd, canonicalUrl } from '@/config/seo';

/** OSM interaktivni embed oko tačke (adresa ↔ marker u sr.js). */
function openStreetMapEmbedUrl(lat, lng, span = 0.006) {
  const minLon = lng - span;
  const minLat = lat - span;
  const maxLon = lng + span;
  const maxLat = lat + span;
  const bbox = `${minLon},${minLat},${maxLon},${maxLat}`;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat}%2C${lng}`;
}

const ContactPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash !== '#kontakt-forma') {
      return;
    }
    const t = window.setTimeout(() => {
      document.getElementById('kontakt-forma')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.hash]);
  const contactInfo = [
    {
      icon: FaMapMarkerAlt,
      title: content.contact.info.addressTitle,
      value: content.company.address,
      link: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(content.company.address)}`,
    },
    {
      icon: FaPhone,
      title: content.contact.info.phoneTitle,
      value: content.company.phone,
      link: `tel:${content.company.phone.replace(/\s/g, '')}`,
    },
    {
      icon: FaEnvelope,
      title: content.contact.info.emailTitle,
      value: content.company.email,
      link: `mailto:${content.company.email}`,
    },
    {
      icon: FaClock,
      title: content.contact.info.workingHoursTitle,
      value: content.company.workingHours,
      link: null,
    },
  ];

  const pageTitle = `Kontakt — ${content.company.address}`;
  const pageDescription =
    `Kontaktirajte ${content.company.fullName}: ${content.company.address}, ` +
    `tel. ${content.company.phone}, ${content.company.email}. ${content.company.workingHours}.`;
  const { address, mapLat, mapLng } = content.company;
  const mapQuery = encodeURIComponent(address);
  const mapEmbedUrl = openStreetMapEmbedUrl(mapLat, mapLng);
  const mapOpenUrl =
    externalUrls.googleMaps || `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;

  const contactPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    url: canonicalUrl('/kontakt'),
    name: `Kontakt — ${content.company.fullName}`,
    about: BUSINESS_REF,
  };

  return (
    <>
      <Seo
        title={pageTitle}
        description={pageDescription}
        path="/kontakt"
        jsonLd={[breadcrumbJsonLd([{ name: 'Kontakt', path: '/kontakt' }]), contactPageJsonLd]}
      />

      <div data-testid="contact-page">
        {/* Hero — isti vizuelni jezik kao O nama / Proizvodi / Projekti */}
        <section className="relative py-32 md:py-40 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
              alt="Kontakt"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-brand-primary/80" />
          </div>
          <div className="container-custom relative z-10 text-center">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="caption text-brand-accent block mb-4"
            >
              {content.contact.caption}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-5xl md:text-7xl font-light text-white mb-4"
            >
              {content.contact.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-neutral-200 text-lg max-w-2xl mx-auto"
            >
              {content.contact.subtitle}
            </motion.p>
          </div>
        </section>

        {/* Contact Content */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Contact Form */}
              <div id="kontakt-forma" className="scroll-mt-28">
                <h2 className="font-heading text-3xl text-brand-primary mb-8">
                  Pošaljite Nam Poruku
                </h2>
                <ContactForm />
              </div>

              {/* Contact Info */}
              <div>
                <h2 className="font-heading text-3xl text-brand-primary mb-8">
                  Kontakt Informacije
                </h2>

                <div className="space-y-6 mb-10">
                  {contactInfo.map((info, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-start gap-4 p-4 bg-neutral-50 hover:bg-brand-bgSoft 
                               transition-colors duration-300"
                      data-testid={`contact-info-${index}`}
                    >
                      <div className="w-12 h-12 rounded-sm bg-brand-accent flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium text-brand-primary mb-1">{info.title}</h4>
                        {info.link ? (
                          <a
                            href={info.link}
                            target={info.link.startsWith('http') ? '_blank' : undefined}
                            rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                            className="text-neutral-700 hover:text-brand-accent transition-colors"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <span className="text-neutral-700">{info.value}</span>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Quick Contact Buttons */}
                <div className="space-y-4">
                  <h3 className="font-heading text-xl text-brand-primary mb-4">
                    Brzi Kontakt
                  </h3>
                  <div className="flex flex-wrap gap-4">
                    <a
                      {...safeExternalAnchor(externalUrls.whatsapp)}
                      className={`flex items-center gap-2 bg-green-500 text-white px-6 py-3 
                               rounded-sm transition-colors
                               ${externalUrls.whatsapp ? 'hover:bg-green-600' : 'opacity-50 cursor-not-allowed'}`}
                      data-testid="whatsapp-button"
                    >
                      <FaWhatsapp size={20} />
                      {content.contact.cta.whatsapp}
                    </a>
                    <a
                      {...safeExternalAnchor(externalUrls.viber)}
                      className={`flex items-center gap-2 bg-purple-600 text-white px-6 py-3 
                               rounded-sm transition-colors
                               ${externalUrls.viber ? 'hover:bg-purple-700' : 'opacity-50 cursor-not-allowed'}`}
                      data-testid="viber-button"
                    >
                      <FaViber size={20} />
                      {content.contact.cta.viber}
                    </a>
                  </div>
                </div>

                {/* Interaktivna mapa (OSM embed — ne zahteva API ključ) */}
                <div className="mt-10">
                  <div className="overflow-hidden rounded-sm border border-neutral-200 bg-neutral-100 shadow-sm">
                    <iframe
                      title={`Mapa lokacije: ${address}`}
                      src={mapEmbedUrl}
                      className="h-[min(50vh,420px)] w-full min-h-[280px] border-0"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                  <a
                    href={mapOpenUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-brand-primary hover:text-brand-accent transition-colors"
                  >
                    <FaMapMarkerAlt className="h-4 w-4 shrink-0" aria-hidden />
                    Otvori u Google Maps
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactPage;
